import { hasBannedCharacters } from '../../lib/validation/text_validation';

/**
 * Validates a user ID string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} user_id The user ID string to validate.
 * @returns {boolean} `true` if the user ID is valid, `false` otherwise.
 */
export const isValidUserId = (user_id: unknown): boolean =>
  typeof user_id === 'string' &&
  !!(
    user_id &&
    !hasBannedCharacters(user_id, {
      banned_characters: '.()&$+%@'
    })
  );

/**
 * Validates a user's first name or last name string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} user_name The first name or last name string to validate.
 * @returns {boolean} `true` if the user name is valid, `false` otherwise.
 */
export const isValidUserName = (user_name: unknown): boolean =>
  typeof user_name === 'string' &&
  !!(
    user_name &&
    !hasBannedCharacters(user_name, {
      banned_characters: '0123456789_,.()&$+%@'
    })
  );

/**
 * Validates a user's email string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} email The email string to validate.
 * @returns {boolean} `true` if the email is valid, `false` otherwise.
 */
export const isValidUserEmail = (email: unknown): boolean => {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return typeof email === 'string' && !!email && emailRegex.test(email);
};

/**
 * Validates a user's phone number string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} phone_number The phone number string to validate.
 * @returns {boolean} `true` if the phone number is valid, `false` otherwise.
 */
export const isValidUserPhoneNumber = (phone_number: unknown): boolean => {
  if (typeof phone_number !== 'string') return false;
  // Phone is optional — empty string passes. Matches the backend behaviour
  // so CSV bulk imports (no phone column) and AddUserDrawer clears both succeed.
  if (phone_number === '') return true;
  const phone_regex = /^\+?\d+$/;
  const is_within_legal_length = phone_number.length >= 4 && phone_number.length <= 17;
  return is_within_legal_length && phone_regex.test(phone_number);
};

/**
 * Validates a user's type string by ensuring it is non-empty and is either "internal", "external" or "any".
 * @param {string} user_type The user type string to validate.
 * @returns {boolean} `true` if the user type is valid, `false` otherwise.
 */
export const isValidUserType = (user_type: unknown): boolean =>
  typeof user_type === 'string' &&
  !!(user_type && (user_type === 'internal' || user_type === 'external' || user_type === 'any'));

/**
 * Validates a user's groups object by ensuring it is non-empty.
 * @param {string} user_groups The groups object to validate.
 * @returns {boolean} `true` if the groups object is valid, `false` otherwise.
 */
export const isValidUserGroups = (user_groups: unknown): boolean =>
  typeof user_groups === 'object' && !!user_groups && !!Object.keys(user_groups).length;
